import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import Hero from '../../components/Hero';
import { getProductBySlug } from '../../data/hvacProducts';
import './ProductDetail.css';

const ProductDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const product = slug ? getProductBySlug(slug) : undefined;

  if (!product) {
    return <Navigate to="/products" replace />;
  }

  return (
    <div className="product-detail">
      <Hero
        title={product.name}
        subtitle={product.tagline}
        background={product.heroBackground}
        fullHeight={false}
      />

      {/* Overview */}
      <section className="section section--dark">
        <div className="container">
          <div className="product-detail__overview">
            <div className="product-detail__visual">
              <img
                src={product.image}
                alt={product.name}
                className="product-detail__image"
              />
            </div>
            <div className="product-detail__content">
              <span className="product__label">{product.category}</span>
              <p className="product__description">{product.description}</p>

              {/* Highlights Grid */}
              <div className="specs__grid">
                {product.highlights.map((h) => (
                  <div key={h.label} className="spec">
                    <span className="spec__value">{h.value}</span>
                    <span className="spec__label">{h.label}</span>
                  </div>
                ))}
              </div>

              {/* Spec Sections */}
              <div className="specs__table">
                {product.specSections.map((section) => (
                  <div key={section.title} className="specs__section">
                    <h4>{section.title}</h4>
                    {section.rows.map((row) => (
                      <div key={row.name} className="specs__row">
                        <span>{row.name}</span>
                        <span>{row.value}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section section--charcoal">
        <div className="container">
          <div className="text-center">
            <span className="section__label">Key Features</span>
            <h2 className="section__title">{product.name} Features</h2>
          </div>
          <div className="product-detail__features">
            {product.features.map((feature) => (
              <div key={feature.title} className="product-detail__feature">
                <div className="product-detail__feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <div>
                  <h3 className="product-detail__feature-title">{feature.title}</h3>
                  <p className="product-detail__feature-text">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section section--dark">
        <div className="container text-center">
          <h2 className="section__title">Interested in the {product.name}?</h2>
          <p className="section__subtitle">
            Contact us for pricing, spec sheets, or to discuss your project requirements.
          </p>
          <div className="btn-group">
            <Link to="/contact" className="btn btn--primary btn--lg">Request a Quote</Link>
            <Link to="/products" className="btn btn--outline-white btn--lg">Browse All Products</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
